import React from 'react'
import type { FC } from 'react'
import { Clock, Eye } from 'lucide-react'
import { format, setDefaultOptions } from 'date-fns'
import { tr } from 'date-fns/locale'
setDefaultOptions({ locale: tr })
import { Badge } from './badge'
import { ViewCounter } from './view-counter'

import type { Post } from '@payload-types'
import { Separator } from '../ui/separator'

interface PostHeaderProps {
  post: Post
  showViewCounter?: boolean
  initialViewCount?: number
  uniqueViewCount?: number
  showDetailedStats?: boolean
}

export const PostHeader: FC<PostHeaderProps> = ({
  post,
  showViewCounter = true,
  initialViewCount,
  uniqueViewCount,
  showDetailedStats = false,
}) => {
  // Handle view count - either use client component or server-rendered count
  const views = initialViewCount || (post as any).views || 0

  // Calculate unique views if not provided
  const uniqueViews =
    uniqueViewCount || ((post as any).uniqueIps ? Object.keys((post as any).uniqueIps).length : 0)

  return (
    <div>
      {/* Tags and metadata */}
      <div className="flex flex-row justify-between items-center my-4">
        <div className="flex flex-row gap-4">
          {/* Categories */}
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-row gap-2 flex-wrap">
              {post.categories.map((category, index) => {
                if (typeof category !== 'object' || !category.title || !category.slug) return null

                return (
                  <Badge
                    key={index}
                    title={category.title}
                    href={`/haberler/${category.slug}`}
                    variant="primary"
                  />
                )
              })}
            </div>
          )}
        </div>

        {post.publishedAt && (
          <div className="flex flex-col items-center gap-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span className="text-xs text-foreground">
                {format(new Date(post.publishedAt), 'd MMMM yyyy, HH:mm')}
              </span>

              {/* Show view count - either client component or server count */}
              {showViewCounter ? (
                <ViewCounter
                  postId={post.id}
                  initialCount={initialViewCount || (post as any).views}
                  uniqueCount={uniqueViews}
                  className="ml-4"
                  showDetailed={showDetailedStats}
                />
              ) : (
                initialViewCount !== undefined && (
                  <div className="flex items-center gap-1 text-gray-600 ml-4">
                    <Eye size={18} />
                    <span className="text-sm">{new Intl.NumberFormat().format(views)}</span>
                  </div>
                )
              )}
            </div>
            {post.contentUpdatedAt && post.contentUpdatedAt !== post.publishedAt && (
              <span className="text-xs text-muted-foreground">
                (Güncellendi: {format(new Date(post.contentUpdatedAt), 'd MMMM yyyy, HH:mm')})
              </span>
            )}
          </div>
        )}
      </div>
      <Separator className="my-2" />
      <h1 className="text-2xl font-bold mb-3">{post.title}</h1>
      {post.spot && <div className="text-sm italic text-muted-foreground">{post.spot}</div>}
      <Separator className="my-2" />
    </div>
  )
}
